import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { User, AuthStep } from '@/types';
import { getCurrentUser, login, logout } from '@/app/services/authApi';
import type { RootState } from '@/app/store';

interface AuthState {
  user: User | null;
  authStep: AuthStep | null;
  isAuthenticated: boolean;
  error: string | null;
  accessTokenExpirationDate: string | null;
  impersonation: boolean;
}

const initialState: AuthState = {
  user: null,
  authStep: null,
  isAuthenticated: false,
  error: null,
  accessTokenExpirationDate: null,
  impersonation: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      if (action.payload) {
        state.authStep = null;
      }
    },
    setAuthStep: (state, action: PayloadAction<AuthStep | null>) => {
      state.authStep = action.payload;
    },
    clearAuth: (state) => {
      state.user = null;
      state.authStep = null;
      state.isAuthenticated = false;
      state.error = null;
      state.accessTokenExpirationDate = null;
      state.impersonation = false;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setAccessTokenExpirationDate: (state, action: PayloadAction<string | null>) => {
      state.accessTokenExpirationDate = action.payload;
    },
    updateAuthHeaders: (
      state,
      action: PayloadAction<{
        authStep?: string | null;
        accessTokenExpirationDate?: string | null;
        impersonation?: boolean;
      }>,
    ) => {
      if (action.payload.authStep !== undefined) {
        state.authStep = (action.payload.authStep as AuthStep | null) || null;
      }
      if (action.payload.accessTokenExpirationDate !== undefined) {
        state.accessTokenExpirationDate = action.payload.accessTokenExpirationDate;
      }
      if (action.payload.impersonation !== undefined) {
        state.impersonation = action.payload.impersonation;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(getCurrentUser.matchFulfilled, (state, action) => {
      const user = action.payload;
      state.user = user;
      state.isAuthenticated = !!user;
      state.error = null;
    });

    builder.addMatcher(getCurrentUser.matchRejected, (state, action) => {
      if ('status' in action.error && action.error.status === 401) {
        state.user = null;
        state.authStep = null;
        state.isAuthenticated = false;
        state.impersonation = false;
      }
      state.error = action.error.message || 'Failed to fetch user';
    });

    builder.addMatcher(login.matchFulfilled, (state, action) => {
      const response = action.payload;
      state.user = response.data;
      state.isAuthenticated = !!response.data;
      state.error = null;
    });

    builder.addMatcher(login.matchRejected, (state, action) => {
      state.error = action.error.message || 'Login failed';
    });

    builder.addMatcher(logout.matchFulfilled, () => {
      // state.user = null;
      // state.authStep = null;
      // state.isAuthenticated = false;
      // state.error = null;
      // state.accessTokenExpirationDate = null;
      // state.impersonation = false;
    });

    builder.addMatcher(getCurrentUser.matchRejected, (state, action) => {
      state.user = null;
      state.authStep = null;
      state.isAuthenticated = false;
      state.error = action.error.message || 'Failed to fetch user';
      state.accessTokenExpirationDate = null;
      state.impersonation = false;
    });
  },
});

export const {
  setUser,
  setAuthStep,
  clearAuth,
  setError,
  setAccessTokenExpirationDate,
  updateAuthHeaders,
} = authSlice.actions;
export const selectAuth = (state: RootState) => state.auth;
export default authSlice.reducer;
